// --- START OF FILE App.js (REWRITTEN, COMPLETE, AND STABLE) ---

import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

// MUI Imports
import { ThemeProvider } from '@mui/material/styles';
import {
    Paper, Typography, Badge, List, ListItem, ListItemText, ListItemIcon,
    Popover, CircularProgress, Chip, Box, IconButton, Tooltip, Menu,
    AppBar, ListItemButton, Toolbar, Avatar, Divider, MenuItem,
    Snackbar, Alert, ListItemSecondaryAction, alpha, Button
} from '@mui/material';
import {
    Apps as AppsIcon, Notifications as NotificationsIcon, ExitToApp as ExitToAppIcon,
    Mail as MailIcon, People as PeopleIcon, LocalShipping as LocalShippingIcon,
    AccountCircle, Print as PrintIcon, ShoppingCart as ShoppingCartIcon, Storage as StorageIcon, Refresh as RefreshIcon,
    Build as BuildIcon, DataObject as DataObjectIcon, Security, Category as CategoryIcon, Checklist as ChecklistIcon, PlaylistAddCheck as PlaylistAddCheckIcon, AccessTime as AccessTimeIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { Forum as ForumIcon } from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TodoWidget from './components/TodoWidget';
import './components/print.css';

// App-specific Imports
import './styles/swal-styles.css';

import NotFoundPage from './pages/NotFoundPage';
import { PublicRoute, InternalRoute } from './components/RouteGuards';
import { getSocket, disconnectSocket } from './utils/socketService';
import { theme } from './theme';
import api from './utils/api';
import { getAppList } from './config/appConfig';
import CommandKMenu from './components/CommandKMenu';
import MyCloudPage from './pages/MyCloudPage';
import { AppProvider, useAppContext } from './contexts/AppContext';
import OrderChat from './components/OrderChat';

// Date/Time Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import Swal from 'sweetalert2';


// --- LAZY-LOADED PAGE COMPONENTS ---
const VisitorsPage = React.lazy(() => import('./components/VisitorsPage'));
const AppDashboard = React.lazy(() => import('./components/AppDashboard'));
const CalendarView = React.lazy(() => import('./components/Calendar'));
const Contacts = React.lazy(() => import('./components/Contacts'));
const Products = React.lazy(() => import('./components/Products'));
const Maintenances = React.lazy(() => import('./components/Maintenances'));
const MaintenanceCalendar = React.lazy(() => import('./components/MaintenanceCalendar'));
const MaintenanceView = React.lazy(() => import('./components/MaintenanceView'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Exemptions = React.lazy(() => import('./components/Exemptions'));
const Login = React.lazy(() => import('./components/Login'));
const Users = React.lazy(() => import('./components/Users'));
const PermissionRoute = React.lazy(() => import('./components/PermissionRoute'));
const BackupRestore = React.lazy(() => import('./components/BackupRestore'));
const BatchLabelPage = React.lazy(() => import('./components/BatchLabelPage'));
const Footer = React.lazy(() => import('./components/Footer'));
const EmailRecipients = React.lazy(() => import('./components/EmailRecipients'));
const Purchases = React.lazy(() => import('./components/Purchases'));
const ReadOnlyMaintenanceDialog = React.lazy(() => import('./components/ReadOnlyMaintenanceDialog'));
const Declarations = React.lazy(() => import('./components/Declarations'));
const Fire = React.lazy(() => import('./components/FireRegistry'));
const Shipments = React.lazy(() => import('./components/Shipments'));

const VacationStats = React.lazy(() => import('./components/VacationStats'));
const DbExplorerPage = React.lazy(() => import('./components/DbExplorerPage'));
const Assignments = React.lazy(() => import('./components/Assignments'));
const SettingsPage = React.lazy(() => import('./components/SettingsPage'));
const RegistrationRequests = React.lazy(() => import('./components/RegistrationRequests'));
const PublicDashboard = React.lazy(() => import('./components/PublicDashboard'));
const AdminOrders = React.lazy(() => import('./components/AdminOrders'));
const Categories = React.lazy(() => import('./components/Categories'));
const EmailQueue = React.lazy(() => import('./components/EmailQueue'));
const GlsTrackingPage = React.lazy(() => import('./pages/GlsTrackingPage'));
const Timbrature = React.lazy(() => import('./pages/Timbrature'));
const OdooPurchaseOrderTrackingPage = React.lazy(() => import('./components/OdooPurchaseOrderTrackingPage'));
const WarehouseDashboardPage = React.lazy(() => import('./components/WarehouseDashboardPage'));
const MinioExplorerPage = React.lazy(() => import('./pages/MinioExplorerPage'));
const NetworkDevicesPage = React.lazy(() => import('./pages/NetworkDevicesPage'));
const DisasterRecoveryPage = React.lazy(() => import('./pages/DisasterRecoveryPage'));
const DeliverySchedulePage = React.lazy(() => import('./components/DeliverySchedulePage'));
const FillSchedulePage = React.lazy(() => import('./pages/FillSchedulePage'));
const Courses = React.lazy(() => import('./components/Courses'));
const PPEAcknowledgment = React.lazy(() => import('./components/PPEAcknowledgment'));
const SupplierCertificates = React.lazy(() => import('./components/SupplierCertificates'));

const InitialRedirector = ({ user, isInitialized }) => {
    const isAuthenticated = !!localStorage.getItem('token');
    if (!isInitialized && isAuthenticated) return null;
    if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
    if (user.userType === 'PUBLIC') return <Navigate to="/my-dashboard" replace />;
    return <Navigate to="/apps" replace />;
};

const LoginRouteHandler = ({ onLogin, user, isInitialized }) => {
    const isAuthenticated = !!localStorage.getItem('token');

    if (!isInitialized && isAuthenticated) return null;

    if (isAuthenticated && user) {
        // Se l'utente è già autenticato, lo reindirizziamo alla sua dashboard.
        // Determina la destinazione in base al tipo di utente, con un fallback a /apps
        const destination = user?.userType === 'PUBLIC' ? '/my-dashboard' : '/apps';
        return <Navigate to={destination} replace />;
    }

    // Se l'utente non è autenticato, mostra la pagina di login.
    return <Login onLogin={onLogin} />;
};

const StagingBanner = () => {
    // process.env.NODE_ENV è una variabile speciale in React.
    // È 'development' quando esegui 'npm start', 'test' per i test, e 'production' dopo la build.
    if (process.env.NODE_ENV !== 'production') {
        const bannerStyle = {
            backgroundColor: '#d32f2f', // Rosso scuro
            color: 'white',
            textAlign: 'center',
            padding: '5px',
            fontWeight: 'bold',
            fontSize: '1rem',
            position: 'fixed', // Fisso in alto
            width: '100%',
            zIndex: 1500, // Sopra la AppBar (che di solito è 1100)
            textTransform: 'uppercase',
            letterSpacing: '1px',
        };

        // Puoi cambiare 'STAGING' in 'DEVELOPMENT' se preferisci
        return <div style={bannerStyle}>Ambiente di Staging / Sviluppo</div>;
    }

    // Se siamo in produzione, non renderizza nulla
    return null;
};

// ==============================================================
//  APPCONTENT COMPONENT
// ==============================================================
function AppContent() {
    // ============================================================== 
    const navigate = useNavigate();
    useEffect(() => {
        document.title = "FlowDesk BSN";
    }, []);

    // --- STATE GLOBALI ---
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
    const isPublicUser = currentUser?.userType === 'PUBLIC';
    const [isAppInitialized, setIsAppInitialized] = useState(false); // Stato per il caricamento
    const [glsShipments, setGlsShipments] = useState([]);
    const [isRefreshingNotifications, setIsRefreshingNotifications] = useState(false);

    // --- STATE UI & NOTIFICHE ---
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [appLogo, setAppLogo] = useState('/logo.png');
    const [notifications, setNotifications] = useState([]);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [isCommandKOpen, setIsCommandKOpen] = useState(false);
    const [updateStatus, setUpdateStatus] = useState({ checking: false, available: false, applying: false, message: '' });
    const [readOnlyMaintenanceDialogOpen, setReadOnlyMaintenanceDialogOpen] = useState(false);
    const [selectedMaintenanceForReadOnly, setSelectedMaintenanceForReadOnly] = useState(null);
    const [ticketNotifications, setTicketNotifications] = useState([]);
    const [drTimerState, setDrTimerState] = useState(null);
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [chatOrder, setChatOrder] = useState(null);
    const [contextualMenu, setContextualMenu] = useState(null); // Conterrà i dati del menu
    const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState(null); // Per l'apertura/chiusura del menu MUI
    const location = useLocation();
    const [productsDialogState, setProductsDialogState] = useState({
        open: false,
        productToEdit: null,
        initialNewName: '',
        forceProductType: '',
        defaultSupplier: null,
        // Aggiungiamo una callback per gestire il salvataggio
        onSave: null
    });
    const [todoAnchorEl, setTodoAnchorEl] = useState(null);
    const [unreadTicketMessages, setUnreadTicketMessages] = useState({});
    const [arrivalNotifications, setArrivalNotifications] = useState([]);
    const [emailNotificationsCount, setEmailNotificationsCount] = useState(0);

    const fetchTicketNotifications = useCallback(async () => {
        try {
            const response = await api.get('/tickets/notifications');

            // IMPORTANTE: Gestisci diverse strutture di risposta possibili
            let notificationsArray = [];

            if (response) {
                // Se response.data è già un array, usalo direttamente
                if (Array.isArray(response.data)) {
                    notificationsArray = response.data;
                }
                // Se response.data è un oggetto con una proprietà 'data' che è un array
                else if (response.data && Array.isArray(response.data.data)) {
                    notificationsArray = response.data.data;
                }
                // Se response stesso è un array (alcune librerie axios fanno così)
                else if (Array.isArray(response)) {
                    notificationsArray = response;
                }
                // Altrimenti usa array vuoto
                else {
                    console.warn('Struttura risposta notifiche inaspettata:', response);
                    notificationsArray = [];
                }
            }

            // Imposta lo stato - a questo punto siamo sicuri che sia un array
            setTicketNotifications(notificationsArray);
            return notificationsArray;

        } catch (err) {
            console.error("Errore durante il fetch delle notifiche ticket:", err);
            setTicketNotifications([]); // Sicurezza: array vuoto in caso di errore
            return [];
        }
    }, []);

    // --- DR TIMER LOGIC ---
    useEffect(() => {
        const checkDr = () => {
            const saved = localStorage.getItem('drBackgroundTest');
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    if (parsed.active) {
                        setDrTimerState(parsed);
                    } else {
                        setDrTimerState(null);
                    }
                } catch { setDrTimerState(null); }
            } else {
                setDrTimerState(null);
            }
        };
        checkDr();
        const interval = setInterval(checkDr, 2000); // Check storage every 2s
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Update time every second if menu is open AND dr is active to show ticking time
        if (notificationsAnchorEl && drTimerState?.isRunning) {
            const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
            return () => clearInterval(interval);
        }
    }, [notificationsAnchorEl, drTimerState?.isRunning]);

    // --- 1. DETERMINA SE LA UI PRINCIPALE DEVE ESSERE VISIBILE ---
    // Definiamo le rotte in cui nascondere AppBar e Footer
    const publicRoutes = ['/login', '/reset-password', '/warehouse-dashboard'];
    // La UI è visibile se il percorso attuale NON inizia con una delle rotte pubbliche
    const showMainUI = !publicRoutes.some(route => location.pathname.startsWith(route));

    // --- DATI SORGENTE PER LE NOTIFICHE ---
    const [dataSources, setDataSources] = useState({
        activeVisitorsCount: 0,
        pendingLabelRequests: [],

        pendingOrders: [],
        pendingRegRequests: [],
        uncategorizedProducts: []
    });

    const handleProductCategorized = useCallback((categorizedProductId) => {

        // Aggiorniamo lo stato rimuovendo il prodotto dalla lista di quelli non categorizzati
        setDataSources(prevDataSources => ({
            ...prevDataSources,
            uncategorizedProducts: prevDataSources.uncategorizedProducts.filter(
                product => product.id !== categorizedProductId
            )
        }));
    }, []);

    const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

    const openChatForOrder = useCallback((order) => {
        setChatOrder(order);
    }, []);

    // La funzione per rimuovere la notifica quando una chat viene aperta
    const handleChatOpen = useCallback((ticketId) => {
        // Ottimismo UI: rimuove subito la notifica dalla lista
        setTicketNotifications(prev => prev.filter(n => n.ticketId !== ticketId));
        // Invia la richiesta al backend per marcare come letto
        api.post(`/tickets/ticket/${ticketId}/read`).catch(err => {
            console.error("Errore nel marcare il ticket come letto:", err);
            // Opzionale: se fallisce, ricarica le notifiche per risincronizzare
            fetchTicketNotifications();
        });
    }, [fetchTicketNotifications]);

    const openProductsDialog = useCallback((config) => {
        setProductsDialogState({
            open: true,
            productToEdit: config.productToEdit || null,
            initialNewName: config.initialNewName || '',
            forceProductType: config.forceProductType || '',
            defaultSupplier: config.defaultSupplier || null,
            onSave: config.onSave || null // Funzione da chiamare quando un prodotto viene creato/aggiornato
        });
    }, []);

    const fetchEmailNotifications = useCallback(async () => {
        try {
            const res = await api.get('/purchase-orders/notifications/count');
            setEmailNotificationsCount(res?.count || 0);
        } catch (e) { console.error(e); }
    }, []);

    const handleMarkAllEmailsRead = useCallback(async () => {
        try {
            await api.post('/purchase-orders/notifications/mark-all-read');
            setEmailNotificationsCount(0);
        } catch (e) {
            console.error('Errore nel segnare tutte le notifiche come lette:', e);
        }
    }, []);

    const contextValue = useMemo(() => ({
        openChatForOrder,
        openProductsDialog,
        glsShipments: [],
        fetchEmailNotifications,
        emailNotificationsCount,
        currentUser,
        isInitialized: isAppInitialized
    }), [openChatForOrder, openProductsDialog, glsShipments, currentUser, fetchEmailNotifications, emailNotificationsCount, isAppInitialized]);

    const safeArray = (data) => {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.data)) return data.data;
        return [];
    };
    // --- FUNZIONE DI INIZIALIZZAZIONE ---
    const initializeApp = useCallback(async (user) => {
        if (!user) return;

        setTicketNotifications(prev => Array.isArray(prev) ? prev : []);

        console.log("🚀 Inizializzazione app per:", user.username);

        const socket = getSocket();
        socket.off(); // Pulisce sempre i listener precedenti

        const updateDataSource = (key, data) => setDataSources(prev => ({ ...prev, [key]: data }));

        // --- INIZIO BLOCCO RIPRISTINATO E CORRETTO ---
        // Questa è la logica "ping-and-fetch" del tuo vecchio codice funzionante.

        fetchEmailNotifications();

        // 2. Ascolta l'evento dal backend
        socket.on('new_email_reply', () => {
            fetchEmailNotifications(); // Ricarica il numero
            Swal.fire({                // Mostra Toast
                toast: true, position: 'top-end', icon: 'info',
                title: 'Nuova risposta ordine', showConfirmButton: false, timer: 5000
            });
        });

        const handleNewTicketNotification = async (notification) => {
            // Mostra subito una notifica toast
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: `Nuovo messaggio per l'ordine #${notification.orderNumber}`,
                text: notification.messagePreview,
                showConfirmButton: false,
                timer: 5000,
                customClass: {
                    popup: 'swal-custom-toast'
                }
            });

            // Aggiorna immediatamente lo stato delle notifiche ticket
            // ricaricandole per avere la lista completa e aggiornata
            await fetchTicketNotifications();
        };

        // Ascolta entrambi gli eventi. Il backend invierà l'evento giusto (new_ticket o new_admin_ticket)
        // a seconda del destinatario, ma il frontend li gestisce allo stesso modo.
        socket.on('new_ticket_notification', handleNewTicketNotification);
        socket.on('new_admin_ticket_notification', handleNewTicketNotification);

        // L'utente si unisce alla sua stanza per ricevere i "ping".
        if (user && user.id) {
            socket.emit('join_user_room', user.id);
        }
        socket.on('new_arrival_notification', (notification) => {
            // Aggiorna lo stato delle notifiche di arrivo
            setArrivalNotifications(prev => [notification, ...prev]);

            const supplierInfo = notification.supplierName ? `Fornitore: ${notification.supplierName}\n` : '';
            const timeInfo = `Registrato da ${notification.registeredBy} alle ${new Date(notification.arrivedAt).toLocaleTimeString('it-IT')}`;

            // Mostra un toast per l'arrivo
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: `Nuovo Arrivo - Ordine #${notification.orderNumber}`,
                text: `${supplierInfo}${timeInfo}`,
                showConfirmButton: false,
                timer: 5000,
                customClass: {
                    popup: 'swal-custom-toast'
                }
            });
        });

        // --- CHIAMATE API INIZIALI (pulite e senza duplicati) ---
        const promises = [];
        const canViewSales = user.isAdmin || user.permissions?.sales?.view;

        promises.push(fetchTicketNotifications());

        if (user.userType !== 'PUBLIC') {
            promises.push(
                api.get('/visitors/all').then(data =>
                    updateDataSource('activeVisitorsCount', safeArray(data).filter(v => !v.exitTime).length)
                )
            );
        }
        if (canViewSales) {
            promises.push(
                api.get('/admin/orders', { params: { status: 'PENDING', pageSize: 9999 } }).then(data =>
                    updateDataSource('pendingOrders', safeArray(data))
                )
            );
        }
        if (user.isAdmin) {
            promises.push(
                api.get('/admin/registration-requests').then(data =>
                    updateDataSource('pendingRegRequests', safeArray(data))
                )
            );
            promises.push(
                api.get('/labels').then(data =>
                    updateDataSource('pendingLabelRequests', safeArray(data).filter(req => req.status !== 'printed'))
                )
            );

            promises.push(
                api.get('/products/uncategorized').then(data =>
                    updateDataSource('uncategorizedProducts', safeArray(data))
                )
            );
        }

        try {
            await Promise.all(promises);

            if (user.userType !== 'PUBLIC') {
                socket.on('visitors-updated', () =>
                    api.get('/visitors').then(data =>
                        updateDataSource('activeVisitorsCount', safeArray(data).filter(v => !v.exitTime).length)
                    )
                );
            }
            if (canViewSales) {
                const handleNewOrder = (order) => {
                    updateDataSource('pendingOrders', prev => [...prev, order]);
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success', // Usiamo 'success' per un nuovo ordine
                        title: 'Nuovo Ordine Ricevuto!',
                        text: `È stato registrato l'ordine #${order.id}`,
                        showConfirmButton: false,
                        timer: 5000,
                        customClass: {
                            popup: 'swal-custom-toast' // Mantiene lo stile personalizzato
                        }
                    });
                };
                socket.on('new_order', handleNewOrder);
                socket.on('order_updated', () => {
                    api.get('/admin/orders', { params: { status: 'PENDING', pageSize: 9999 } }).then(data =>
                        updateDataSource('pendingOrders', safeArray(data))
                    );
                });
            }
            if (user.isAdmin) {
                const handleNewUncategorized = (newProducts) => {
                    updateDataSource('uncategorizedProducts', prev => [...prev, ...newProducts]);
                    Swal.fire({
                        icon: 'info',
                        title: 'Nuovi Prodotti Creati',
                        text: `${newProducts.length} prodotti richiedono una categoria.`,
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 5000
                    });
                };

                socket.on('new_registration_request', (req) =>
                    updateDataSource('pendingRegRequests', prev => [req, ...prev])
                );
                socket.on('registration_request_processed', () =>
                    api.get('/admin/registration-requests').then(data =>
                        updateDataSource('pendingRegRequests', safeArray(data))
                    )
                );
                socket.on('new_label_request', () =>
                    api.get('/labels').then(data =>
                        updateDataSource('pendingLabelRequests', safeArray(data).filter(req => req.status !== 'printed'))
                    )
                );
                socket.on('update_label_request', () =>
                    api.get('/labels').then(data =>
                        updateDataSource('pendingLabelRequests', safeArray(data).filter(req => req.status !== 'printed'))
                    )
                );

                socket.on('new_uncategorized_products', handleNewUncategorized);
                socket.on('product_categorized', handleProductCategorized);
            }
        } catch (error) {
            console.error("Errore durante l'inizializzazione dei dati:", error);
        } finally {
            setIsAppInitialized(true);
        }
    }, [fetchTicketNotifications, handleProductCategorized]);


    // Anche nella funzione refreshNotifications:
    const refreshNotifications = useCallback(async () => {
        if (!currentUser) return; // Non fare nulla se non c'è un utente
        console.log("🔄 Eseguo refresh di TUTTE le notifiche...");
        setIsRefreshingNotifications(true); // Attiva l'indicatore di caricamento
        fetchEmailNotifications();

        const canViewSales = currentUser.isAdmin || currentUser.permissions?.sales?.view;
        const promises = [];

        // 1. Ricarica le notifiche dei ticket
        promises.push(fetchTicketNotifications());

        // 2. Ricarica le notifiche generiche (visitatori, ordini, etc.)
        if (currentUser.userType !== 'PUBLIC') {
            promises.push(
                api.get('/visitors/all').then(data =>
                    setDataSources(prev => ({ ...prev, activeVisitorsCount: safeArray(data).filter(v => !v.exitTime).length }))
                )
            );
        }
        if (canViewSales) {
            promises.push(
                api.get('/admin/orders', { params: { status: 'PENDING', pageSize: 9999 } }).then(data =>
                    setDataSources(prev => ({ ...prev, pendingOrders: safeArray(data) }))
                )
            );
        }
        if (currentUser.isAdmin) {
            promises.push(api.get('/admin/registration-requests').then(data => setDataSources(prev => ({ ...prev, pendingRegRequests: safeArray(data) }))));
            promises.push(api.get('/labels').then(data => setDataSources(prev => ({ ...prev, pendingLabelRequests: safeArray(data).filter(req => req.status !== 'printed') }))));
            promises.push(api.get('/products/uncategorized').then(data => setDataSources(prev => ({ ...prev, uncategorizedProducts: safeArray(data) }))));
        }

        try {
            await Promise.all(promises);
            // Non è necessario fare nulla nel 'then', lo stato è già stato aggiornato
        } catch (error) {
            console.error("Errore durante il refresh manuale delle notifiche:", error);
            showSnackbar('Errore nel ricaricare le notifiche', 'error');
        } finally {
            setIsRefreshingNotifications(false); // Disattiva l'indicatore di caricamento
        }
    }, [currentUser, fetchTicketNotifications]);

    const cleanupApp = useCallback(() => {
        console.log("🧹 Eseguo pulizia app e listeners.");
        const socket = getSocket();
        socket.off();
        setDataSources({ activeVisitorsCount: 0, pendingLabelRequests: [], pendingOrders: [], pendingRegRequests: [], uncategorizedProducts: [] });
    }, []);

    // --- EFFETTO PRINCIPALE ---
    useEffect(() => {
        const checkAuth = async () => {
            if (isAuthenticated && !currentUser) {
                try {
                    const user = await api.get('/me');
                    setCurrentUser(user);
                } catch (error) {
                    console.error("Session verification failed:", error);
                    handleLogout();
                    setIsAppInitialized(true);
                }
            } else if (!isAuthenticated) {
                setIsAppInitialized(true); // Se non autenticato, app "pronta" per mostrare il login
            }
        };
        checkAuth();
    }, [isAuthenticated, currentUser]);

    useEffect(() => {
        if (isAuthenticated && currentUser) {
            // Se non è ancora stata inizializzata per questo utente, avviala
            if (!isAppInitialized) {
                initializeApp(currentUser);
                setIsAppInitialized(true); // Segna come inizializzata
            }
        } else {
            // Se l'utente fa logout, resetta lo stato di inizializzazione
            if (isAppInitialized) {
                cleanupApp();
            }
        }
    }, [isAuthenticated, currentUser, isAppInitialized, initializeApp, cleanupApp]);


    const handleLogin = ({ user, token }) => {
        localStorage.setItem('token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // PRIMA: Reset esplicito degli stati UI
        setUserMenuAnchorEl(null);
        setNotificationsAnchorEl(null);

        // DOPO: Cleanup e impostazione nuovi stati
        cleanupApp(); // Pulisce lo stato precedente

        setCurrentUser(user);
        setIsAuthenticated(true);
        setIsAppInitialized(false); // Forza esplicitamente la re-inizializzazione

        if (user.userType === 'PUBLIC') navigate('/my-dashboard', { replace: true });
        else navigate('/apps', { replace: true });
    };



    const handleLogout = () => {
        localStorage.clear();
        setCurrentUser(null);
        setIsAuthenticated(false);
        disconnectSocket();
        navigate('/login');
    };

    // --- HANDLERS UI E FUNZIONI SECONDARIE ---
    const handleUserMenuOpen = (event) => setUserMenuAnchorEl(event.currentTarget);
    const handleUserMenuClose = () => setUserMenuAnchorEl(null);
    const handleNotificationsOpen = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
        // Avvia il refresh quando l'utente clicca la campanella
        refreshNotifications();
    };
    const handleNotificationsClose = () => setNotificationsAnchorEl(null);
    const handleNotificationClick = (path, state = {}) => {
        handleNotificationsClose();
        navigate(path, { state });
    };
    const hasPermission = useCallback((resource) => currentUser?.isAdmin || currentUser?.permissions?.[resource]?.view === true, [currentUser]);
    const handleForgotPassword = async () => {
        if (!currentUser || !currentUser.email) {
            Swal.fire('Errore', 'Impossibile procedere, email utente non trovata.', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Cambia Password',
            html: `Stai per richiedere un reset della password. Verrà inviato un link all'indirizzo <b>${currentUser.email}</b>.<br/><br/>Vuoi procedere?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sì, invia link',
            cancelButtonText: 'Annulla'
        });

        if (result.isConfirmed) {
            // Chiudi il menu utente prima di mostrare la notifica di successo
            setUserMenuAnchorEl(null);

            try {
                // Il payload è semplice, contiene solo l'email dell'utente corrente
                const response = await api.post('/users/forgot-password', { email: currentUser.email });

                Swal.fire({
                    title: 'Controlla la tua Email',
                    text: response.message,
                    icon: 'success'
                });
            } catch (error) {
                Swal.fire('Errore', error.response?.data?.message || 'Impossibile inviare la richiesta di reset.', 'error');
            }
        }
    };

    useEffect(() => {
        const fetchAppLogo = async () => {
            try {
                const response = await api.get('/settings/logo');
                if (response?.data?.logoUrl) setAppLogo(`${response.data.logoUrl}?v=${new Date().getTime()}`);
            } catch (error) { console.error("Errore recupero logo:", error.message); }
        };
        if (isAuthenticated) fetchAppLogo();
    }, [isAuthenticated]);

    useEffect(() => {
        const handleKeyDown = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setIsCommandKOpen(p => !p); } };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const visibleAppsForCommandK = useMemo(() => {
        if (!currentUser) return [];
        try { return getAppList(theme).filter(app => hasPermission(app.resource)); } catch { return []; }
    }, [currentUser, theme, hasPermission]);

    useEffect(() => {
        if (!isAuthenticated) {
            setContextualMenu(null);
            return;
        }
        // Controlla il percorso e imposta il menu appropriato
        if (location.pathname.startsWith('/suppliers')) {
            setContextualMenu({
                title: 'Fornitori',
                items: [
                    { label: 'Vai a Prodotti', path: '/products' },
                    { label: 'Vai agli Acquisti', path: '/purchases' },
                    { label: 'Vai ai Visitatori', path: '/visitors' },
                    { label: 'Vai alle Manutenzioni', path: '/maintenances' }
                ]
            });
        } else if (location.pathname.startsWith('/products')) {
            setContextualMenu({
                title: 'Prodotti',
                items: [
                    { label: 'Vai a Fornitori', path: '/suppliers' },
                    { label: 'Vai agli Acquisti', path: '/purchases' }
                ]
            });

        } else if (location.pathname.startsWith('/assignments')) {
            setContextualMenu({
                title: 'Dotazioni',
                items: [
                    { label: 'Vai a Destinatari', path: '/recipients' },
                    { label: 'Vai ai Prodotti', path: '/products' }
                ]
            });

        } else if (location.pathname.startsWith('/maintenances')) {
            setContextualMenu({
                title: 'Manutenzioni',
                items: [
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai ai Contatti', path: '/contacts' },
                    { label: 'Vai ai Visitatori', path: '/visitors' },
                ]
            });

        } else if (location.pathname.startsWith('/contacts')) {
            setContextualMenu({
                title: 'Contatti',
                items: [
                    { label: 'Vai alle Esenzioni', path: '/exemptions' },
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai alle Spedizioni', path: '/shipments' },
                    { label: 'Vai alle Dichiarazioni', path: '/declarations' }
                ]
            });
        } else if (location.pathname.startsWith('/shipments')) {
            setContextualMenu({
                title: 'Spedizioni/Ritiri',
                items: [
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai ai Contatti', path: '/contacts' },
                    { label: 'Vai ai Contatti (Fornitori)', path: '/contacts' }
                ]
            });
        } else if (location.pathname.startsWith('/purchases')) {
            setContextualMenu({
                title: 'Acquisti',
                items: [
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai ai Contatti', path: '/contacts' }
                ]
            });

        } else if (location.pathname.startsWith('/exemptions')) {
            setContextualMenu({
                title: 'Esenzioni',
                items: [
                    { label: 'Vai ai Contatti', path: '/contacts' },
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai ai Destinatari', path: '/recipients' }
                ]
            });
        } else if (location.pathname.startsWith('/declarations')) {
            setContextualMenu({
                title: 'Dichiarazioni',
                items: [
                    { label: 'Vai ai Contatti', path: '/contacts' },
                ]
            });
        } else if (location.pathname.startsWith('/fire')) {
            setContextualMenu({
                title: 'Ispezioni Antincendio',
                items: [
                    { label: 'Vai ai Prodotti', path: '/products' },
                    { label: 'Vai ai Destinatari', path: '/recipients' }
                ]
            });
        } else if (location.pathname.startsWith('/recipients')) {
            setContextualMenu({
                title: 'Destinatari Email',
                items: [
                    { label: 'Vai alle Esenzioni', path: '/exemptions' },
                    { label: 'Vai alle Spedizioni', path: '/shipments' },
                    { label: 'Vai alle Ispezioni Antincendio', path: '/fire' },
                    { label: 'Vai alle Dotazioni', path: '/assignments' },
                    { label: 'Vai agli Acquisti', path: '/purchases' },
                ]
            });
        } else if (location.pathname.startsWith('/visitors')) {
            setContextualMenu({
                title: 'Visitatori',
                items: [
                    { label: 'Vai alle Manutenzioni', path: '/maintenances' },
                ]
            });


        } else {
            // Se non siamo in una sezione con menu contestuale, lo nascondiamo
            setContextualMenu(null);
        }
    }, [location.pathname, isAuthenticated]);

    // --- EFFETTO PER COSTRUIRE LE NOTIFICHE ---
    useEffect(() => {
        const newNotifications = [];
        if (!isAuthenticated || !dataSources) return;

        if (!isPublicUser && dataSources.activeVisitorsCount > 0) newNotifications.push({ id: 'visitors', icon: <PeopleIcon color="warning" />, text: `${dataSources.activeVisitorsCount} visitatori presenti`, description: "Ci sono ancora visitatori nella struttura.", path: '/visitors', count: dataSources.activeVisitorsCount });
        if (currentUser?.isAdmin) {
            if (dataSources.pendingOrders.length > 0) newNotifications.push({ id: 'orders', icon: <ShoppingCartIcon color="primary" />, text: `${dataSources.pendingOrders.length} nuovi ordini`, description: "Clienti hanno inviato ordini da processare.", path: '/admin/orders', count: dataSources.pendingOrders.length });
            if (dataSources.pendingRegRequests.length > 0) newNotifications.push({ id: 'requests', icon: <MailIcon color="secondary" />, text: `${dataSources.pendingRegRequests.length} richieste`, description: "Nuovi utenti attendono approvazione.", path: '/admin/requests', count: dataSources.pendingRegRequests.length });
            if (dataSources.pendingLabelRequests.length > 0) newNotifications.push({ id: 'labels', icon: <PrintIcon />, text: `${dataSources.pendingLabelRequests.length} etichette in attesa`, description: "Etichette in attesa di essere stampate.", path: '/batch-label', state: { openTab: 3, highlightPending: true }, count: dataSources.pendingLabelRequests.length });

            if (dataSources.uncategorizedProducts.length > 0) newNotifications.push({ id: 'uncategorized_products', icon: <CategoryIcon color="action" />, text: `${dataSources.uncategorizedProducts.length} Prodotti da Categorizzare`, description: "Prodotti creati da Odoo senza categoria.", path: '/products', state: { highlight: 'uncategorized', filter: 'uncategorized' }, count: dataSources.uncategorizedProducts.length });
            if (emailNotificationsCount > 0) {
                newNotifications.push({
                    id: 'email_replies',
                    icon: <ForumIcon color="info" />,
                    text: `${emailNotificationsCount} Risposte Ordini`,
                    path: '/po-tracking',
                    state: { filter: 'UNREAD_MESSAGES' },
                    count: emailNotificationsCount
                });
            }
        }

        if (drTimerState && drTimerState.active && (currentUser?.isAdmin || currentUser?.permissions?.disasterRecovery?.view)) {
            let seconds = drTimerState.accumulatedTime || 0;
            if (drTimerState.isRunning && drTimerState.startTime) {
                const diff = Math.floor((Date.now() - drTimerState.startTime) / 1000);
                seconds += diff;
            }
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

            newNotifications.push({
                id: 'dr_timer',
                icon: <AccessTimeIcon color="warning" />,
                text: `Cronometro DR: ${timeStr}`,
                description: "Test Disaster Recovery in corso",
                path: '/disaster-recovery',
                count: 1
            });
        }

        setNotifications(newNotifications);
    }, [dataSources, isAuthenticated, currentUser, isPublicUser, drTimerState, currentTime, emailNotificationsCount]);

    if (!isAppInitialized && isAuthenticated) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography>Caricamento dati...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    const totalTicketNotifications = ticketNotifications.length;
    const totalGenericNotifications = notifications.length;
    const totalArrivalNotifications = arrivalNotifications.length;
    const totalAllNotifications = totalGenericNotifications + totalTicketNotifications + totalArrivalNotifications;
    const handleTodoOpen = (e) => setTodoAnchorEl(e.currentTarget);
    const handleTodoClose = () => setTodoAnchorEl(null);
    const handleContextMenuOpen = (event) => setContextMenuAnchorEl(event.currentTarget);
    const handleContextMenuClose = () => setContextMenuAnchorEl(null);

    return (
        <>
            <StagingBanner />
            <AppProvider value={contextValue}>
                <ThemeProvider theme={theme}>
                    {showMainUI && (
                        <AppBar position="sticky"
                            sx={{ top: process.env.NODE_ENV !== 'production' ? '25px' : 0 }}>
                            <Toolbar sx={{ justifyContent: 'space-between' }}>
                                <Box component={Link} to={isPublicUser ? "/my-dashboard" : "/"} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                    <img src={appLogo} alt="Logo" style={{ height: 35, marginRight: 16 }} />
                                </Box>
                                {contextualMenu && (
                                    <Box sx={{ ml: 2 }}>
                                        <Button
                                            color="inherit"
                                            onClick={handleContextMenuOpen}
                                            endIcon={<AppsIcon />} // O un'altra icona a tua scelta
                                            sx={{ textTransform: 'none', fontSize: '1rem' }}
                                        >
                                            {contextualMenu.title}
                                        </Button>
                                        <Menu
                                            anchorEl={contextMenuAnchorEl}
                                            open={Boolean(contextMenuAnchorEl)}
                                            onClose={handleContextMenuClose}
                                        >
                                            {contextualMenu.items.map((item, index) => (
                                                <MenuItem
                                                    key={index}
                                                    component={Link}
                                                    to={item.path}
                                                    onClick={handleContextMenuClose}
                                                >
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isAuthenticated && !isPublicUser && (
                                        <Tooltip title="Home">
                                            <span>
                                                <IconButton color="inherit" component={Link} to="/apps">
                                                    <AppsIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isAuthenticated && currentUser?.userType === 'INTERNAL' && (
                                        <Tooltip title="Todo List">
                                            <span>
                                                <IconButton color="inherit" onClick={handleTodoOpen}>
                                                    <ChecklistIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isAuthenticated && (
                                        <Tooltip title="Notifiche">
                                            <span>
                                                <IconButton color="inherit" onClick={handleNotificationsOpen} disabled={totalAllNotifications === 0 && !isRefreshingNotifications}>
                                                    {isRefreshingNotifications ? (
                                                        <CircularProgress size={24} color="inherit" />
                                                    ) : (
                                                        <Badge badgeContent={totalAllNotifications} color="error">
                                                            <NotificationsIcon />
                                                        </Badge>
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isAuthenticated && currentUser ? (
                                        <>
                                            <Tooltip title="Account Utente">
                                                <IconButton onClick={handleUserMenuOpen} size="large" sx={{ p: 0, ml: 1 }}>
                                                    <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.main' }}>{currentUser.username.charAt(0).toUpperCase()}</Avatar>
                                                </IconButton>
                                            </Tooltip>
                                            <Menu
                                                anchorEl={userMenuAnchorEl}
                                                open={Boolean(userMenuAnchorEl) && userMenuAnchorEl.isConnected !== false}
                                                onClose={handleUserMenuClose}>
                                                <Box sx={{ px: 2, py: 1.5 }}>
                                                    <Typography variant="subtitle1" noWrap>{currentUser.firstName || currentUser.username}</Typography>
                                                    <Typography variant="body2" color="text.secondary" noWrap>{currentUser.isAdmin ? 'Amministratore' : (isPublicUser ? 'Cliente' : 'Utente')}</Typography>
                                                </Box>
                                                <Divider />
                                                {/* Voce di menu visibile a TUTTI gli utenti loggati, eccetto quelli pubblici */}
                                                {!isPublicUser && (
                                                    <MenuItem component={Link} to="/my-cloud" onClick={handleUserMenuClose}>
                                                        <ListItemIcon><StorageIcon fontSize="small" /></ListItemIcon>
                                                        Il Mio Cloud
                                                    </MenuItem>

                                                )}
                                                {hasPermission('sales') && (
                                                    <MenuItem component={Link} to="/delivery-schedule" onClick={handleUserMenuClose}>
                                                        <ListItemIcon><LocalShippingIcon fontSize="small" /></ListItemIcon>
                                                        Pianificazione Consegne
                                                    </MenuItem>
                                                )}
                                                {hasPermission('timestamps') && (
                                                    <MenuItem component={Link} to="/timestamps" onClick={handleUserMenuClose}>
                                                        <ListItemIcon><AccessTimeIcon fontSize="small" /></ListItemIcon>
                                                        Timbrature
                                                    </MenuItem>
                                                )}


                                                {/* Aggiungiamo un divisore se l'utente è admin o non-pubblico per separare
                                        dalle opzioni admin sottostanti o dalle impostazioni generali */}
                                                {(currentUser.isAdmin || !isPublicUser) && <Divider />}
                                                {currentUser.isAdmin && (
                                                    <Box>
                                                        <MenuItem component={Link} to="/users" onClick={handleUserMenuClose}><ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>Gestione Utenti</MenuItem>
                                                        <MenuItem component={Link} to="/admin/requests" onClick={handleUserMenuClose}><ListItemIcon><MailIcon fontSize="small" /></ListItemIcon>Richieste</MenuItem>
                                                        <MenuItem component={Link} to="/admin/db-explorer" onClick={handleUserMenuClose}><ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>Esploratore DB</MenuItem>
                                                        <MenuItem component={Link} to="/admin/minio-explorer" onClick={handleUserMenuClose}>
                                                            <ListItemIcon><StorageIcon fontSize="small" /></ListItemIcon>Gestione Cloud
                                                        </MenuItem>
                                                        <MenuItem component={Link} to="/admin/categories" onClick={handleUserMenuClose}><ListItemIcon><CategoryIcon fontSize="small" /></ListItemIcon>Categorie prodotti</MenuItem>
                                                        <MenuItem component={Link} to="/admin/email-queue" onClick={handleUserMenuClose}>
                                                            <ListItemIcon><MailIcon fontSize="small" /></ListItemIcon>Coda Email
                                                        </MenuItem>
                                                        <MenuItem component={Link} to="/admin/settings" onClick={handleUserMenuClose}><ListItemIcon><BuildIcon fontSize="small" /></ListItemIcon>Impostazioni</MenuItem>
                                                    </Box>
                                                )}
                                                <MenuItem onClick={() => { handleUserMenuClose(); handleForgotPassword(); }}>
                                                    <ListItemIcon><Security fontSize="small" /></ListItemIcon>
                                                    Cambia Password
                                                </MenuItem>
                                                <Divider />
                                                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                                    <ListItemIcon><ExitToAppIcon fontSize="small" color="error" /></ListItemIcon> Logout
                                                </MenuItem>
                                            </Menu>
                                        </>
                                    ) : (<Button component={Link} to="/login" variant="outlined">Login</Button>)}
                                </Box>
                            </Toolbar>
                        </AppBar>
                    )}
                    <Popover
                        open={Boolean(todoAnchorEl)}
                        anchorEl={todoAnchorEl}
                        onClose={handleTodoClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: { width: 500, maxHeight: 500, p: 0 } }}
                    >
                        <TodoWidget />
                    </Popover>
                    <Popover
                        open={Boolean(notificationsAnchorEl)}
                        anchorEl={notificationsAnchorEl}
                        onClose={handleNotificationsClose}
                        PaperProps={{ sx: { width: 380, mt: 1.5, borderRadius: 2, boxShadow: 5 } }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6">Notifiche</Typography>
                            <Tooltip title="Aggiorna notifiche">
                                <span>
                                    <IconButton onClick={refreshNotifications} size="small" disabled={isRefreshingNotifications}>
                                        {isRefreshingNotifications ? <CircularProgress size={20} /> : <RefreshIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                            {/* Renderizza prima le notifiche dei ticket */}
                            {ticketNotifications && ticketNotifications.length > 0 && ticketNotifications.map((notif) => (
                                <ListItemButton
                                    key={`ticket-${notif.ticketId}`}
                                    onClick={() => {
                                        handleNotificationsClose();
                                        // Ora chiamiamo la funzione globale con un oggetto che ha la stessa
                                        // struttura di un oggetto 'order' (basta id e orderNumber)
                                        openChatForOrder({ id: notif.orderId, orderNumber: notif.orderNumber });
                                    }}
                                >
                                    <ListItemIcon><MailIcon color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary={<Typography variant="body2" fontWeight="bold">Ordine #{notif.orderNumber}</Typography>}
                                        secondary={`${notif.senderName}: "${notif.messagePreview}"`}
                                    />
                                </ListItemButton>
                            ))}

                            {/* Renderizza le notifiche di arrivo (NUOVO BLOCCO) */}
                            {arrivalNotifications && arrivalNotifications.length > 0 && arrivalNotifications.map((notif, index) => (
                                <ListItemButton
                                    key={`arrival-${notif.orderId}-${index}`} // Chiave unica
                                    onClick={() => {
                                        handleNotificationsClose();
                                        navigate('/po-tracking', {
                                            state: {
                                                highlightOrderId: notif.orderId,       // L'ID per l'evidenziazione
                                                highlightOrderNumber: notif.orderNumber  // Il numero per la ricerca
                                            }
                                        });
                                        // Rimuovi questa notifica specifica dopo averla cliccata
                                        setArrivalNotifications(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    sx={{ alignItems: 'flex-start', py: 1.5 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                        <LocalShippingIcon color="success" /> {/* Icona per gli arrivi */}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={<Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="body1" sx={{ fontWeight: 500 }}>Nuovo Arrivo: Ordine #{notif.orderNumber} {notif.supplierName ? `${notif.supplierName}` : ''}</Typography></Box>}
                                        secondary={<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Registrato da {notif.registeredBy} alle {new Date(notif.arrivedAt).toLocaleTimeString('it-IT')}</Typography>}
                                    />
                                </ListItemButton>
                            ))}

                            {/* Divisore se ci sono entrambi i tipi di notifiche */}
                            {ticketNotifications.length > 0 && notifications.length > 0 && <Divider />}

                            {/* Renderizza le notifiche generiche */}
                            {notifications && notifications.length > 0 && notifications.map((notif) => (
                                <ListItemButton key={notif.id} onClick={() => handleNotificationClick(notif.path, notif.state)} sx={{ alignItems: 'flex-start', py: 1.5 }}>
                                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{notif.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={<Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="body1" sx={{ fontWeight: 500 }}>{notif.text}</Typography><Chip label={notif.count} size="small" color="error" /></Box>}
                                        secondary={notif.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{notif.description}</Typography>}
                                    />
                                    {notif.id === 'email_replies' && (
                                        <Tooltip title="Segna tutte come lette">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); handleMarkAllEmailsRead(); }}
                                                sx={{ ml: 1, color: 'success.main', flexShrink: 0, alignSelf: 'center' }}
                                            >
                                                <DoneAllIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </ListItemButton>
                            ))}

                            {totalAllNotifications === 0 && (
                                <ListItem><ListItemText primary="Nessuna nuova notifica" sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }} /></ListItem>
                            )}
                        </List>
                    </Popover>
                </ThemeProvider>
                <ThemeProvider theme={theme}>


                    {/* Main content */}
                    <Box component="main" sx={{
                        flexGrow: 1,
                        py: 3,
                        px: { xs: 1, sm: 2, md: 3 },
                        bgcolor: 'background.default',
                        minHeight: 'calc(100vh - 64px - 50px)',
                        paddingTop: process.env.NODE_ENV !== 'production' ? '30px' : 3
                    }}>
                        <Suspense fallback={
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                                <CircularProgress />
                            </Box>
                        }>
                            <Routes>
                                {/* ========== ROUTE PUBBLICHE (SENZA AUTENTICAZIONE) ========== */}
                                {/* Queste DEVONO essere prima di tutto il resto */}
                                <Route path="/login" element={<LoginRouteHandler onLogin={handleLogin} user={currentUser} isInitialized={isAppInitialized} />} />
                                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                                <Route path="/fill-schedule/:accessToken" element={<FillSchedulePage />} />
                                <Route path="/warehouse-dashboard" element={<WarehouseDashboardPage />} />
                                <Route path="/acknowledge/:token" element={<PPEAcknowledgment />} />

                                {/* ========== ROUTE PER UTENTI PUBBLICI AUTENTICATI ========== */}
                                <Route
                                    path="/my-dashboard"
                                    element={
                                        <PublicRoute>
                                            <PublicDashboard
                                                currentUser={currentUser}
                                                ticketNotifications={ticketNotifications}
                                            />
                                        </PublicRoute>
                                    }
                                />

                                {/* ========== ROUTE PER UTENTI INTERNI ========== */}
                                <Route
                                    path="/*"
                                    element={
                                        <InternalRoute>
                                            <Routes>
                                                <Route
                                                    path="/admin/minio-explorer"
                                                    element={
                                                        <PermissionRoute resource="dbExplorer" isAdminOnly={true}>
                                                            <MinioExplorerPage />
                                                        </PermissionRoute>
                                                    }
                                                />
                                                <Route path="/apps" element={<AppDashboard currentUser={currentUser} hasPermission={hasPermission} />} />
                                                <Route path="/dashboard" element={<PermissionRoute resource="dashboard"><Dashboard /></PermissionRoute>} />
                                                <Route path="/visitors" element={<PermissionRoute resource="visitors"><VisitorsPage /></PermissionRoute>} />
                                                <Route path="/calendar" element={<PermissionRoute resource="calendars"><CalendarView /></PermissionRoute>} />
                                                <Route path="/contacts" element={<PermissionRoute resource="customers"><Contacts /></PermissionRoute>} />
                                                <Route path="/products" element={<PermissionRoute resource="products"><Products onDataChange={refreshNotifications} onProductCategorized={handleProductCategorized} /></PermissionRoute>} />
                                                <Route path="/maintenances" element={<PermissionRoute resource="maintenances"><MaintenanceView /></PermissionRoute>} />
                                                <Route path="/exemptions" element={<PermissionRoute resource="exemptions"><Exemptions /></PermissionRoute>} />
                                                <Route path="/batch-label" element={<PermissionRoute resource="labels"><BatchLabelPage /></PermissionRoute>} />
                                                <Route path="/recipients" element={<PermissionRoute resource="recipients"><EmailRecipients /></PermissionRoute>} />
                                                <Route path="/purchases" element={<PermissionRoute resource="purchases"><Purchases /></PermissionRoute>} />
                                                <Route path="/courses" element={<PermissionRoute resource="courses"><Courses /></PermissionRoute>} />
                                                <Route path="/supplier-certificates" element={<PermissionRoute resource="supplierCertificates"><SupplierCertificates /></PermissionRoute>} />
                                                <Route path="/declarations" element={<PermissionRoute resource="declarations"><Declarations /></PermissionRoute>} />
                                                <Route path="/fire" element={<PermissionRoute resource="fire"><Fire /></PermissionRoute>} />
                                                <Route path="/shipments" element={<PermissionRoute resource="shipments"><Shipments /></PermissionRoute>} />

                                                <Route path="/assignments" element={<PermissionRoute resource="assignments"><Assignments /></PermissionRoute>} />
                                                <Route path="/users" element={<PermissionRoute resource="users"><Users /></PermissionRoute>} />
                                                <Route path="/admin/requests" element={<PermissionRoute resource="users"><RegistrationRequests /></PermissionRoute>} />
                                                <Route path="/admin/orders" element={<PermissionRoute resource="sales"><AdminOrders currentUser={currentUser} ticketNotifications={Array.isArray(ticketNotifications) ? ticketNotifications : []} onDataChange={refreshNotifications} /></PermissionRoute>} />
                                                <Route path="/admin/backup" element={<PermissionRoute resource="backups"><BackupRestore /></PermissionRoute>} />
                                                <Route path="/admin/db-explorer" element={<PermissionRoute resource="dbExplorer"><DbExplorerPage /></PermissionRoute>} />
                                                <Route path="/admin/vacations" element={<PermissionRoute resource="vacations"><VacationStats /></PermissionRoute>} />
                                                <Route path="/admin/settings" element={<PermissionRoute resource="settings"><SettingsPage /></PermissionRoute>} />
                                                <Route path="/admin/categories" element={<Categories />} />
                                                <Route path="/my-cloud" element={<MyCloudPage />} />
                                                <Route path="/admin/email-queue" element={<PermissionRoute resource="emailQueue" isAdminOnly={true}><EmailQueue /></PermissionRoute>} />
                                                <Route path="/gls-tracking" element={<PermissionRoute resource="glsTracking"><GlsTrackingPage /></PermissionRoute>} />
                                                <Route path="/po-tracking" element={<PermissionRoute resource="poTracking"><OdooPurchaseOrderTrackingPage /></PermissionRoute>} />
                                                <Route
                                                    path="/delivery-schedule"
                                                    element={
                                                        <PermissionRoute resource="sales">
                                                            <DeliverySchedulePage />
                                                        </PermissionRoute>
                                                    }
                                                />
                                                <Route path="/timestamps" element={<PermissionRoute resource="timestamps"><Timbrature /></PermissionRoute>} />
                                                <Route
                                                    path="/network-devices"
                                                    element={
                                                        <PermissionRoute resource="networkDevices">
                                                            <NetworkDevicesPage />
                                                        </PermissionRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/disaster-recovery"
                                                    element={
                                                        <PermissionRoute resource="disasterRecovery">
                                                            <DisasterRecoveryPage />
                                                        </PermissionRoute>
                                                    }
                                                />
                                                <Route path="*" element={<NotFoundPage />} />
                                            </Routes>
                                        </InternalRoute>
                                    }
                                />

                                {/* ========== REDIRECT INIZIALE (SOLO PER "/") ========== */}
                                {/* IMPORTANTE: Questa route DEVE essere l'ultima! */}
                                <Route path="/" element={<InitialRedirector user={currentUser} isInitialized={isAppInitialized} />} />
                            </Routes>
                        </Suspense>
                    </Box>

                    <CommandKMenu open={isCommandKOpen} onClose={() => setIsCommandKOpen(false)} apps={visibleAppsForCommandK} />
                    <ReadOnlyMaintenanceDialog open={readOnlyMaintenanceDialogOpen} onClose={() => setReadOnlyMaintenanceDialogOpen(false)} maintenanceData={selectedMaintenanceForReadOnly} />
                    <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
                        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
                    </Snackbar>
                    <OrderChat
                        open={!!chatOrder}
                        onClose={() => setChatOrder(null)}
                        order={chatOrder}
                        currentUser={currentUser}
                        onChatOpen={handleChatOpen}
                    />
                    <Products
                        open={productsDialogState.open}
                        onClose={() => setProductsDialogState(prev => ({ ...prev, open: false }))}
                        productToEdit={productsDialogState.productToEdit}
                        initialNewName={productsDialogState.initialNewName}
                        forceProductType={productsDialogState.forceProductType}
                        defaultSupplier={productsDialogState.defaultSupplier}
                        // Passiamo la callback 'onSave' ai gestori corretti
                        onProductCreated={(newProduct) => {
                            if (productsDialogState.onSave) {
                                productsDialogState.onSave(newProduct);
                            }
                            setProductsDialogState(prev => ({ ...prev, open: false })); // Chiudi dopo aver salvato
                        }}
                        onProductUpdated={(updatedProduct) => {
                            if (productsDialogState.onSave) {
                                productsDialogState.onSave(updatedProduct);
                            }
                            setProductsDialogState(prev => ({ ...prev, open: false })); // Chiudi dopo aver salvato
                        }}
                    />
                </ThemeProvider>
            </AppProvider>
        </>
    );
}

// ==============================================================
//  MAIN APP COMPONENT
// ==============================================================
function App() {
    return (
        <DndProvider backend={HTML5Backend}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                <Router>
                    <AppContent />
                </Router>
            </LocalizationProvider>
        </DndProvider>
    );
}

export default App;
